import { type PriceIntentAnimalBreedFragment } from '../graphql/generated'
import type { Label } from './PriceCalculator.types'

export const MIXED_BREED_OPTION_ID = '-1'
export const UNKNOWN_BREED_ID = 'OKAND_VET_INTE'

type BaseField<ValueType> = {
  name: string
  label: Label
  required?: boolean
  defaultValue?: ValueType
}

export type TextField = BaseField<string> & {
  type: 'text'
  pattern?: string
  minLength?: number
  maxLength?: number
  inputMode?: 'text' | 'numeric' | 'tel' | 'email'
  inputType?: 'text' | 'tel' | 'email'
}

export type NumberField = BaseField<number> & {
  type: 'number'
  min?: number
  max?: number
  suffix?: string
}

export type DateField = BaseField<string> & {
  type: 'date'
  min?: string
  max?: string
}

type RadioField = BaseField<string> & {
  type: 'radio'
  options: Array<FieldOption>
  // Defaults to 'horizontal'
  stacking?: 'horizontal' | 'vertical'
  // Defaults to 'true'
  displayLabel?: boolean
}

export type FieldOption = {
  label: Label
  value: string
}

type SelectField = BaseField<string> & {
  type: 'select'
  options: Array<FieldOption>
}

export type MaskedAddressField = BaseField<string> & {
  name: 'zipCode' | 'street'
  type: 'masked-address'
  pattern?: string
  minLength?: number
  maxLength?: number
  inputMode?: 'text' | 'numeric'
}

export type ExtraBuildingsField = BaseField<Array<ExtraBuilding>> & {
  type: 'extra-buildings'
  buildingOptions: Array<FieldOption>
}

export type UseRegistrationAddressField = {
  type: 'use-registration-address'
  name: string
  required: true
  value?: boolean
  label: Label
  defaultValue?: boolean
}

export type ExtraBuilding = { area: number; type: string; hasWaterConnected: boolean }

export type CarRegistrationNumberField = BaseField<string> & {
  type: 'car-registration-number'
}

export type CarMileageField = BaseField<string> & {
  type: 'car-mileage'
}

export type CurrentInsuranceField = BaseField<string> & {
  type: 'current-insurance'
}

// This is not a form field per se as it doesn't hold data to be used during form submission,
// so even though it can have things like a 'name', it's not gonna be used for anything.
// We include it to be consistent with other fields + make TS happy.
export type CurrentInsuranceFieldReadOnly = Omit<BaseField<string>, 'label'> & {
  type: 'current-insurance-readonly'
}

export type StepperField = BaseField<number> & {
  type: 'stepper'
  min?: number
  max: number
  valueLabel: Label
}

export type Breed = Pick<PriceIntentAnimalBreedFragment, 'id' | 'displayName'>

export type PetCatBreedsField = BaseField<Array<Breed['id']>> & {
  type: 'pet-cat-breeds'
}

export type PetDogBreedsField = BaseField<Array<Breed['id']>> & {
  type: 'pet-dog-breeds'
}

export type InputField =
  | TextField
  | NumberField
  | DateField
  | RadioField
  | SelectField
  | UseRegistrationAddressField
  | ExtraBuildingsField
  | CarRegistrationNumberField
  | CarMileageField
  | CurrentInsuranceField
  | CurrentInsuranceFieldReadOnly
  | StepperField
  | PetCatBreedsField
  | PetDogBreedsField
  | MaskedAddressField
